/*
 * Copyright (c) 2023-2025 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import { message } from 'antd'
import React, { ReactNode, createContext, useContext, useMemo } from 'react'
import { Notification } from '../components/Notification/Notification'

enum NotificationKey {
  INFO = 'INFO',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  WARNING = 'WARNING',
  LOADING = 'LOADING'
}

const DEFAULT_NOTIFICATIONS_CONFIGS = {
  className: 'flex items-center justify-end',
  duration: 7.5
}

export interface NotificationParam {
  title: ReactNode | string
  content?: React.ReactNode | string
}

interface ContextType {
  info: (params: NotificationParam) => void
  success: (params: NotificationParam) => void
  error: (params: NotificationParam) => void
  warning: (params: NotificationParam) => void
  loading: (params: Pick<NotificationParam, 'title'>) => void
  stopLoading: () => void
  destroy(key?: React.Key): void
}

const defaultContextValue: ContextType = {
  info: () => {},
  success: () => {},
  error: () => {},
  warning: () => {},
  loading: () => {},
  stopLoading: () => {},
  destroy: () => {}
}

const context = createContext<ContextType>(defaultContextValue)

export function useNotification() {
  return useContext(context)
}

export function NotificationProvider({ children, notificationDuration }: { children: ReactNode; notificationDuration?: number }) {
  const [messageApi, contextHolder] = message.useMessage()

  const memoizedMessageApi = useMemo(() => {
    return {
      // When showing success/error, always destroy the loading notification.
      success: params => {
        messageApi.destroy(NotificationKey.LOADING)
        messageApi.success({
          ...DEFAULT_NOTIFICATIONS_CONFIGS,
          duration: notificationDuration ?? DEFAULT_NOTIFICATIONS_CONFIGS.duration,
          key: NotificationKey.SUCCESS,
          content: <Notification {...params} notificationKey={NotificationKey.SUCCESS} />
        })
      },
      error: params => {
        messageApi.destroy(NotificationKey.LOADING)
        messageApi.error({
          ...DEFAULT_NOTIFICATIONS_CONFIGS,
          duration: notificationDuration ?? DEFAULT_NOTIFICATIONS_CONFIGS.duration,
          key: NotificationKey.ERROR,
          content: <Notification {...params} notificationKey={NotificationKey.ERROR} />
        })
      },
      info: params => {
        messageApi.info({
          ...DEFAULT_NOTIFICATIONS_CONFIGS,
          duration: notificationDuration ?? DEFAULT_NOTIFICATIONS_CONFIGS.duration,
          key: NotificationKey.INFO,
          content: <Notification {...params} notificationKey={NotificationKey.INFO} />
        })
      },
      warning: params => {
        messageApi.warning({
          ...DEFAULT_NOTIFICATIONS_CONFIGS,
          duration: notificationDuration ?? DEFAULT_NOTIFICATIONS_CONFIGS.duration,
          key: NotificationKey.WARNING,
          content: <Notification {...params} notificationKey={NotificationKey.WARNING} />
        })
      },
      // Set the duration for loading notification to 0 because we can't predict network speed.
      loading: params => {
        messageApi.loading({
          ...DEFAULT_NOTIFICATIONS_CONFIGS,
          key: NotificationKey.LOADING,
          duration: 0,
          content: params.title
        })
      },
      stopLoading: () => messageApi.destroy(NotificationKey.LOADING),
      destroy: messageApi.destroy
    } as ContextType
  }, [messageApi])

  return (
    <context.Provider value={memoizedMessageApi}>
      {contextHolder}

      {children}
    </context.Provider>
  )
}
